import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { cn } from '@/lib/utils';
import { Gift, Loader2, Ticket } from 'lucide-react';

type PrizeKind = 'merch' | 'pro';
type ClaimStatus = 'denied' | 'claimed' | 'checkout' | 'pending' | 'granted' | 'unopened';
type FilterKind = 'all' | PrizeKind;

export interface ChestPrizeRow {
    id: string;
    kind: PrizeKind;
    subtype: string | null;
    source: string | null;
    user_id: string;
    event_at: string;
    delivered: boolean;
    deny_reason: string | null;
    claim_status: ClaimStatus;
    order_number: string | null;
    order_status: string | null;
    paid_at: string | null;
    display_name: string;
    email: string | null;
}

export interface ChestPrizeSummary {
    merch_attempts: number;
    merch_granted: number;
    merch_denied: number;
    merch_pending: number;
    merch_claimed: number;
    pro_unopened: number;
    pro_unclaimed: number;
}

const SUBTYPE_LABEL: Record<string, string> = {
    merch_camiseta_corta: 'Camiseta manga corta',
    merch_camiseta_larga: 'Camiseta manga larga',
    merch_sudadera: 'Sudadera',
    merch_gorra: 'Gorra',
    pro_1w: 'Pro 1 semana',
    pro_1m: 'Pro 1 mes',
    pro_3m: 'Pro 3 meses',
    pro_6m: 'Pro 6 meses',
    pro_1y: 'Pro 1 año',
    pro_forever: 'Pro para siempre',
};

const SOURCE_LABEL: Record<string, string> = {
    capture_chest: 'Captura',
    battle_chest: 'Batalla',
    epesca_chest: 'e-Pesca cofre',
    epesca_battle: 'e-Pesca batalla',
    quiz_reward: 'Quiz',
    transmute: 'Quiz / legado',
    inventory: 'Inventario',
    pending_capture: 'Cofre captura sin abrir',
    pending_battle: 'Cofre batalla sin abrir',
};

const STATUS_LABEL: Record<ClaimStatus, string> = {
    denied: 'No entregado',
    claimed: 'Reclamado',
    checkout: 'Pedido iniciado',
    pending: 'Sin reclamar',
    granted: 'Entregado',
    unopened: 'Cofre sin abrir',
};

function unwrapPayload(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (Array.isArray(raw)) {
        const first = raw[0];
        return first && typeof first === 'object' ? (first as Record<string, unknown>) : {};
    }
    return typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
}

function asNumber(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function normalizeTracking(raw: unknown): { summary: ChestPrizeSummary; rows: ChestPrizeRow[] } {
    const obj = unwrapPayload(raw);
    const summaryRaw = (obj.summary && typeof obj.summary === 'object' ? obj.summary : {}) as Record<
        string,
        unknown
    >;
    const rowsRaw = Array.isArray(obj.rows) ? obj.rows : [];
    return {
        summary: {
            merch_attempts: asNumber(summaryRaw.merch_attempts),
            merch_granted: asNumber(summaryRaw.merch_granted),
            merch_denied: asNumber(summaryRaw.merch_denied),
            merch_pending: asNumber(summaryRaw.merch_pending),
            merch_claimed: asNumber(summaryRaw.merch_claimed),
            pro_unopened: asNumber(summaryRaw.pro_unopened),
            pro_unclaimed: asNumber(summaryRaw.pro_unclaimed),
        },
        rows: rowsRaw
            .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
            .map((row) => ({
                id: String(row.id ?? ''),
                kind: row.kind === 'pro' ? 'pro' : 'merch',
                subtype: row.subtype != null ? String(row.subtype) : null,
                source: row.source != null ? String(row.source) : null,
                user_id: String(row.user_id ?? ''),
                event_at: String(row.event_at ?? ''),
                delivered: row.delivered === true,
                deny_reason: row.deny_reason != null ? String(row.deny_reason) : null,
                claim_status: (row.claim_status as ClaimStatus) || 'pending',
                order_number: row.order_number != null ? String(row.order_number) : null,
                order_status: row.order_status != null ? String(row.order_status) : null,
                paid_at: row.paid_at != null ? String(row.paid_at) : null,
                display_name: String(row.display_name ?? 'Usuario'),
                email: row.email != null ? String(row.email) : null,
            })),
    };
}

function formatWhen(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function statusClass(status: ClaimStatus): string {
    if (status === 'claimed') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (status === 'denied') return 'bg-red-500/10 text-red-400 border-red-500/20';
    if (status === 'checkout') return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
    if (status === 'unopened') return 'bg-sky-500/10 text-sky-300 border-sky-500/20';
    return 'bg-muted/40 text-muted-foreground border-border';
}

export function ChestPrizeTrackingTab() {
    const [filter, setFilter] = useState<FilterKind>('all');
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin', 'chest-prize-tracking'],
        queryFn: async () => {
            const { data: raw, error: rpcError } = await dbClient.rpc('admin_list_chest_prize_tracking', {
                p_limit: 200,
            });
            if (rpcError) throw rpcError;
            return normalizeTracking(raw);
        },
    });

    const rows = useMemo(() => {
        const list = data?.rows ?? [];
        if (filter === 'all') return list;
        return list.filter((row) => row.kind === filter);
    }, [data?.rows, filter]);

    const summary = data?.summary;

    if (isLoading) {
        return (
            <div className="py-16 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="px-4 py-3 rounded-xl border bg-red-500/5 border-red-500/20 text-sm text-red-400">
                No se pudo cargar el seguimiento: {(error as Error).message}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Premios Pro y merch que han salido en cofres: si se entregaron, a quién, y si el usuario los
                reclamó. El Pro reclamado desaparece del inventario; aquí ves los que siguen pendientes o sin
                abrir.
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryCard
                    label="Merch entregado"
                    value={summary?.merch_granted ?? 0}
                    sub={`${summary?.merch_denied ?? 0} denegados de ${summary?.merch_attempts ?? 0} intentos`}
                    icon={<Gift size={16} className="text-fuchsia-400" />}
                />
                <SummaryCard
                    label="Merch reclamado"
                    value={summary?.merch_claimed ?? 0}
                    sub={`${summary?.merch_pending ?? 0} sin reclamar o en checkout`}
                />
                <SummaryCard
                    label="Pro sin reclamar"
                    value={summary?.pro_unclaimed ?? 0}
                    sub="Ya en inventario, aún no activado"
                    icon={<Ticket size={16} className="text-amber-400" />}
                />
                <SummaryCard
                    label="Pro sin abrir"
                    value={summary?.pro_unopened ?? 0}
                    sub="Salió en el cofre y aún no se abrió"
                />
            </div>

            <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-muted/30 border border-border/50 w-fit">
                {(
                    [
                        ['all', 'Todos'],
                        ['merch', 'Merch'],
                        ['pro', 'Pro'],
                    ] as const
                ).map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setFilter(key)}
                        className={cn(
                            'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors',
                            filter === key
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                            <tr>
                                <th className="text-left font-medium px-3 py-2">Usuario</th>
                                <th className="text-left font-medium px-3 py-2">Premio</th>
                                <th className="text-left font-medium px-3 py-2">Origen</th>
                                <th className="text-left font-medium px-3 py-2">Fecha</th>
                                <th className="text-left font-medium px-3 py-2">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                                        No hay premios Pro ni merch registrados aún.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <tr key={row.id} className="border-t border-border/60">
                                        <td className="px-3 py-2 align-top">
                                            <div className="font-medium">{row.display_name}</div>
                                            <div className="text-xs text-muted-foreground">{row.email || '—'}</div>
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <div>{SUBTYPE_LABEL[row.subtype ?? ''] ?? row.subtype ?? '—'}</div>
                                            {row.kind === 'merch' && (
                                                <div className="text-[11px] text-fuchsia-300/80">Merch</div>
                                            )}
                                            {row.kind === 'pro' && (
                                                <div className="text-[11px] text-amber-300/80">Cupón Pro</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 align-top text-muted-foreground">
                                            {SOURCE_LABEL[row.source ?? ''] ?? row.source ?? '—'}
                                        </td>
                                        <td className="px-3 py-2 align-top font-mono text-xs text-muted-foreground whitespace-nowrap">
                                            {formatWhen(row.event_at)}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <span
                                                className={cn(
                                                    'inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium',
                                                    statusClass(row.claim_status)
                                                )}
                                            >
                                                {STATUS_LABEL[row.claim_status]}
                                            </span>
                                            {row.deny_reason && (
                                                <div className="text-[11px] text-red-400/80 mt-1">{row.deny_reason}</div>
                                            )}
                                            {row.order_number && (
                                                <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                                                    {row.order_number}
                                                    {row.order_status ? ` · ${row.order_status}` : ''}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    label,
    value,
    sub,
    icon,
}: {
    label: string;
    value: number;
    sub: string;
    icon?: ReactNode;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
                {icon}
            </div>
            <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
            <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
        </div>
    );
}
